import { useRef, useState } from 'react';
import { nanoid } from 'nanoid';

import { fetchMapboxSuggestions } from '@widget/api/fetchMapboxSuggestions';
import { mapMapboxSuggestion } from '../utils/mapMapboxSuggestion';

import type { Suggestion } from '../types';

type RequestOptions = {
  type: 'street' | 'address' | string;
};

const MAPBOX_SESSION_LIMIT = 50;

export function useAddressSuggestions(value: string, { market }: { market: string }) {
  const sessionToken = useRef({ uid: nanoid(), requestsCount: 0 });
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  const fetchSuggestions = async (search: string, { type }: RequestOptions) => {
    if (search.length < 2) {
      return [];
    }

    setLoading(true);

    try {
      sessionToken.current.requestsCount++;
      if (sessionToken.current.requestsCount > MAPBOX_SESSION_LIMIT) {
        sessionToken.current = { uid: nanoid(), requestsCount: 0 };
      }

      const fetchedSuggestions = await fetchMapboxSuggestions(search, {
        sessionToken: sessionToken.current.uid,
        country: market,
        type,
      });

      const mappedSuggestions = fetchedSuggestions.map(mapMapboxSuggestion);
      setSuggestions(mappedSuggestions);
      setLoading(false);

      return mappedSuggestions;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('mapbox api error', error);
      setLoading(false);
      return suggestions;
    }
  };

  const resetSuggestions = () => {
    setSuggestions([]);
  };

  return { suggestions, loading, resetSuggestions, fetchSuggestions };
}
